import { useEffect } from 'react';
import { tenantSlugFromPathOrHost } from '../utils/tenantDomains';

/**
 * Keeps the <link rel="manifest"> in sync with the tenant slug in the URL.
 *
 * - Root route (/, /auth, /admin, ...) → platform manifest (/manifest.json).
 * - Tenant route (/:slug/app, /:slug/auth, etc.) → tenant-branded manifest
 *   served by the backend at /api/tenants/public/:slug/manifest.json, so
 *   the installed home-screen icon shows THAT tenant's name + logo.
 *
 * Re-runs on every path change (listens to popstate + pushstate hooks).
 * Returns null — purely a side-effect component.
 */

function extractSlug(pathname) {
  return tenantSlugFromPathOrHost(pathname);
}

function setManifestHref(href) {
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
}

function setHeadLink(rel, href, attrs = {}) {
  if (!href) return;
  let link = document.querySelector(`link[rel="${rel}"][data-tenant-install="true"]`)
    || document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    link.dataset.tenantInstall = 'true';
    document.head.appendChild(link);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    if (value) link.setAttribute(key, value);
    else link.removeAttribute(key);
  });
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
}

const KNOWN_TENANT_BRANDS = {
  mystical: 'MYSTICAL REEL GEMS',
  starlight: 'STARLIGHT',
  startlight: 'STARLIGHT',
};

function setMeta(name, content) {
  if (!content) return;
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function setPropertyMeta(property, content) {
  if (!content) return;
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function titleFromSlug(slug) {
  const key = String(slug || '').toLowerCase();
  if (KNOWN_TENANT_BRANDS[key]) return KNOWN_TENANT_BRANDS[key];
  return key
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'TeamLobby';
}

function withCacheKey(url, key) {
  if (!url || url.startsWith('data:')) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}v=${encodeURIComponent(key)}`;
}

export function tenantFaviconHref(slug) {
  if (!slug) return '/favicon.ico';
  return `/api/tenants/public/${encodeURIComponent(slug)}/favicon`;
}

export function pickTenantBrand(tenant, slug) {
  const branding = tenant?.branding || {};
  const theme = branding.theme || {};
  const homeScreen = branding.home_screen || {};
  const name = String(
    homeScreen.app_name ||
    theme.brand_name ||
    branding.brand_name ||
    tenant?.name ||
    slug ||
    'TeamLobby'
  ).trim();
  const logo = String(
    homeScreen.icon_url ||
    theme.logo_url ||
    theme.intro_logo_url ||
    branding.logo_url ||
    tenantFaviconHref(slug)
  ).trim();
  const color = String(
    theme.btn_primary_from ||
    theme.neon ||
    branding.theme_color ||
    branding.primary_color ||
    '#0B0E14'
  ).trim();
  const updated = homeScreen.updated_at || branding.theme_updated_at || branding.updated_at || tenant?.updated_at || slug || 'brand';
  return { name, logo: withCacheKey(logo, updated), color };
}

async function fetchTenantBrand(slug, signal) {
  const res = await fetch(`/api/tenants/public/${encodeURIComponent(slug)}`, {
    credentials: 'omit',
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Tenant brand fetch failed');
  return res.json();
}

function applyInstallBrand({ name, logo, color }) {
  document.title = name;
  const description = `${name} player portal.`;
  setMeta('application-name', name);
  setMeta('apple-mobile-web-app-title', name);
  setMeta('theme-color', color);
  setMeta('description', description);
  setMeta('apple-mobile-web-app-capable', 'yes');
  setMeta('mobile-web-app-capable', 'yes');
  setPropertyMeta('og:site_name', name);
  setPropertyMeta('og:title', name);
  setPropertyMeta('og:description', description);
  setMeta('twitter:title', name);
  setMeta('twitter:description', description);
  setHeadLink('apple-touch-icon', logo);
  setHeadLink('icon', logo, { type: '' });
}

function manifestHrefForSlug(slug) {
  if (
    typeof window !== 'undefined'
    && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    && String(slug || '').toLowerCase() === 'demo'
  ) {
    return '/manifest.json';
  }
  if (String(slug || '').includes('.')) return '/manifest.json';
  return `/api/tenants/public/${encodeURIComponent(slug)}/manifest.json`;
}

export default function TenantManifestLink() {
  useEffect(() => {
    let activeController = null;

    const apply = () => {
      const slug = extractSlug(window.location.pathname);
      if (activeController) activeController.abort();
      if (slug) {
        applyInstallBrand({
          name: titleFromSlug(slug),
          logo: tenantFaviconHref(slug),
          color: '#0B0E14',
        });
        setManifestHref(manifestHrefForSlug(slug));
        activeController = new AbortController();
        fetchTenantBrand(slug, activeController.signal)
          .then(tenant => applyInstallBrand(pickTenantBrand(tenant, slug)))
          .catch((err) => {
            if (err?.name === 'AbortError') return;
            applyInstallBrand({
              name: slug,
              logo: tenantFaviconHref(slug),
              color: '#0B0E14',
            });
          });
  } else {
        setManifestHref('/manifest.json');
        applyInstallBrand({
          name: 'PlayEMP',
          logo: '/favicon.ico',
          color: '#07090f',
        });
      }
    };
    apply();
    // react-router mutates history without firing popstate, so wrap pushState
    // to detect SPA navigations too.
    const originals = {};
    const wrap = (method) => {
      const orig = history[method];
      originals[method] = orig;
      history[method] = function (...args) {
        const r = orig.apply(this, args);
        apply();
        return r;
      };
    };
    wrap('pushState');
    wrap('replaceState');
    window.addEventListener('popstate', apply);
    return () => {
      if (activeController) activeController.abort();
      Object.entries(originals).forEach(([method, orig]) => {
        history[method] = orig;
      });
      window.removeEventListener('popstate', apply);
    };
  }, []);

  return null;
}
